import React, { useState, useContext, useEffect } from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { PrismicRichText } from "@prismicio/react";
import { useInView } from "react-intersection-observer";
import { GatsbyImage } from "gatsby-plugin-image";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

// SEO
import { PageSeo } from "../../components/global/page-seo";

// Components
import { DetermineModule } from "../../components/utils/determine-module";
import {
  CustomCarouselLayout,
  normaliseCarouselDocument,
} from "../../components/modules/custom-carousel";

// Context
import { CurrentLanguage } from "../../components/context/current-language";
import { HeaderColor } from "../../components/context/header-color";
import { PageColor } from "../../components/context/page-color";
import { PageContext } from "../../components/context/page-context";

const Content = styled.div`
    padding: 100px 40px 90px 40px;

    @media (max-width: 800px) {
      padding: 31px 25px;
    }

    & .title-container {
      grid-column: 1 / 13;

      &.section-title {
        margin: 60px 0 40px 0;
      }
    }

    & .gallery-second-image {
      aspect-ratio: 7 / 5;
      max-width: 100%;
    }

    & .gallery-info-block {
      margin: 0 auto;
      max-width: 1280px;
      padding-top: 90px;
      justify-content: 20px;

      @media (max-width: 800px) {
        flex-direction: column;
      }

      & .gallery-main-image {
        aspect-ratio: 5/7;
        grid-column: 1 / 4;
        border: 39px solid #fff;
        box-sizing: border-box;

        @media (max-width: 800px) {
          margin: 0 auto;
          grid-column: 1 / 7;
        }
      }

      & .gallery-info {
        padding: 50px 0;
        grid-column: 5 / 7;
        display: flex;
        flex-direction: column;
        gap: 40px;

        @media (max-width: 800px) {
          width: 100%;
          grid-column: 1 / 7;
          padding:0;
        }

        & .gallery-title-wrapper {
          border-bottom: 1px solid #09184f;
          display: flex;
          flex-direction: row;
          align-items: center;
          flex-wrap: no-wrap;
          justify-content: space-between;

          & .button {
            border: 1px solid #09184f;
            padding: 8px 12px;

            @media (max-width: 800px) {
              padding: 5px 0;
              width:100%;
              text-align: center;
            }
          }
          @media (max-width: 800px) {
            flex-direction: column;
            align-items: start;
            gap:10px;
            padding: 0 0 20px 0;
          }
        }

        & .gallery-hours {
          padding: 20px 0;
          max-width: 100%;

          & .announcement-block {
            display:block;
            background-color:rgba(9,24,79,0.05);
            margin-top:20px;
            text-align: center;
            width:100%;
            padding: 10px 20px;
            color: #09184f;
            font-style: italic;
          }

          & .opening-hour {
            display: flex;
            flex-direction: row;
            flex-wrap: no-wrap;
            justify-content: start;
            column-gap:40px;
            margin: 5px 0;

            & .day-label {
            width:40%;
            }
          }
          & .opening-hour:first-child {
            margin-top: 20px;
          }
        }
      }
    }

    & .grid-6 {
      @media (max-width: 800px) {
        grid-row-gap:50px;
      }
  `;

const SliceZone = styled.div`
  & .product-row-carousel {
    background-color: #ffffff;
  }
`;

const GallerySingle = ({ data }) => {
  const gallery = data.prismicGalleriesSingle;

  // Same source as gatsby-node.js uses to build the `/{lang}/event/category/…`
  // routes, so the button below cannot drift from them.
  const langPrefix = gallery.lang.split("-")[0];

  const [currentLanguage, setCurrentLanguage] = useContext(CurrentLanguage);

  const [pageColor, setPageColor] = useContext(PageColor);
  const [headerColor, setHeaderColor] = useContext(HeaderColor);
  const [pageContext, setPageContext] = useContext(PageContext);

  const labelsEntries = data.prismicSiteLabels?.data?.labels || [];

  const labels = Object.fromEntries(
    labelsEntries
      .filter((item) => item.key && item.value)
      .map((item) => [item.key, item.value])
  );

  useEffect(() => {
    setPageColor(`#efefef`);
    setHeaderColor(`#efefef`);
  }, []);

  const dayKeys = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return (
    <div>
      <Content>
        <div className="section-title title-container">
          <h1>{gallery.data.title?.text}</h1>
        </div>
        <h3>{gallery.data.galleries_single_text?.text}</h3>
        <div className="gallery-info-block grid-6">
          <img
            className="gallery-main-image"
            src={gallery.data.galleries_single_image?.url}
            alt={gallery.data.galleries_single_image.alt || ""}
          />
          <div className="gallery-info">
            <div className="gallery-title-wrapper">
              <h2>{labels?.info}</h2>
              {gallery.data.galleries_email && (
                <a
                  className="button"
                  href={`mailto:${gallery.data.galleries_email}`}
                >
                  {labels?.contact_gallery_label &&
                    `${labels.contact_gallery_label} - `}
                  {gallery.data.title?.text}
                </a>
              )}
            </div>
            <div className="gallery-address">
              {gallery.data.galleries_address && (
                <h4>{gallery.data.galleries_address}</h4>
              )}
              {gallery.data.galleries_address_2 && (
                <h4>{gallery.data.galleries_address_2}</h4>
              )}
              {(gallery.data.galleries_zip ||
                gallery.data.galleries_city ||
                gallery.data.galleries_country) && (
                <h4>
                  {gallery.data.galleries_zip} {gallery.data.galleries_city},{" "}
                  {gallery.data.galleries_country}
                </h4>
              )}
              {gallery.data.galleries_phone && (
                <h4>{gallery.data.galleries_phone}</h4>
              )}
            </div>
            <div className="gallery-hours">
              <h3>{labels?.opening_hours_title}</h3>
              {gallery.data.announcement && (
                <div className="announcement-block">
                  {gallery.data.announcement}
                </div>
              )}
              {gallery.data.by_appointment ? (
                <h4>{labels?.by_appointment}</h4>
              ) : (
                gallery.data.galleries_single_hours?.length > 0 && (
                  <ul className="opening-hours">
                    {gallery.data.galleries_single_hours.map((hour, index) => {
                      const dayKey = dayKeys[index];
                      const dayLabel = labels?.[dayKey] || dayKey;

                      return (
                        <li key={index} className="opening-hour">
                          <i className="day-label">{dayLabel}</i>
                          <i>
                            {hour.galleries_single_hours_closing
                              ? `${hour.galleries_single_hours_opening} ${labels?.to_hour} ${hour.galleries_single_hours_closing}`
                              : labels?.closed ||
                                hour.galleries_single_hours_opening}
                          </i>
                        </li>
                      );
                    })}
                  </ul>
                )
              )}
            </div>
          </div>
        </div>
        {/* {gallery.data.galleries_single_image_2?.url && (
          <img
            className="gallery-second-image"
            src={gallery.data.galleries_single_image_2.url}
            alt={gallery.data.galleries_single_image_2.alt || ""}
          />
        )} */}
      </Content>
      <SliceZone>
        {gallery.data.body?.map((content, index) => (
          <DetermineModule
            key={`gallery_slice_${index}_${gallery.id}`}
            content={content}
            index={index}
            pageType="gallery"
            currentLanguage={currentLanguage}
            labels={labels}
          />
        ))}
      </SliceZone>

      {/* Every event attached to this gallery, with no slice to fill in. The
          title and the button label have no editor field, so they are written
          out in the two languages of the site; the button points at the event
          category page, exactly where the hand-made carousel used to. Renders
          nothing when the gallery has no event. */}
      <CustomCarouselLayout
        items={data.allPrismicEvent.nodes.map((doc) =>
          normaliseCarouselDocument(doc, labels)
        )}
        title={langPrefix === `fr` ? `Événements` : `Events`}
        buttonText={
          langPrefix === `fr` ? `Voir tous les événements` : `See all events`
        }
        buttonLink={`/${langPrefix}/event/category/${gallery.uid}`}
      />
    </div>
  );
};

export const query = graphql`
  query GallerySinglePage($uid: String!, $lang: String!) {
    prismicGalleriesSingle(uid: { eq: $uid }, lang: { eq: $lang }) {
      uid
      lang
      data {
        title {
          text
        }
        galleries_single_text {
          text
        }
        galleries_single_image {
          url
          alt
        }
        galleries_address
        galleries_address_2
        galleries_zip
        galleries_city
        galleries_country
        galleries_phone
        galleries_email
        by_appointment
        announcement
        galleries_single_hours {
          galleries_single_hours_opening
          galleries_single_hours_closing
        }
        body {
          ... on PrismicGalleriesSingleDataBodyNewsletter {
            id
            slice_type
            primary {
              newsletter {
                document {
                  ... on PrismicNewsletterSignup {
                    id
                    data {
                      background_color
                      email_placeholder
                      newsletter_text {
                        richText
                      }
                    }
                  }
                }
              }
            }
          }

          ... on PrismicGalleriesSingleDataBodyProductRowCarousel {
            id
            slice_type
            primary {
              product_row_title {
                richText
              }
            }
            items {
              product {
                document {
                  ... on PrismicProduct {
                    id
                    url
                    data {
                      thumbnail {
                        gatsbyImageData(aspectRatio: 0.71)
                        alt
                      }
                      title {
                        text
                      }
                      price_on_demand
                      shopify_product {
                        title
                        handle
                        id
                        variants {
                          price
                        }
                      }
                    }
                  }
                }
                url
                id
              }
            }
          }
          ... on PrismicGalleriesSingleDataBodyCustomCarousel {
            id
            slice_type
            primary {
              carousel_title {
                text
              }
              button_text
            }
            items {
              content_link {
                document {
                  ... on PrismicEvent {
                    id
                    url
                    type
                    lang
                    data {
                      title {
                        text
                      }
                      date_begins
                      date_ends
                      featured_img {
                        alt
                        gatsbyImageData(aspectRatio: 0.71)
                      }
                      categories {
                        category {
                          uid
                        }
                      }
                    }
                  }
                  ... on PrismicProject {
                    id
                    url
                    type
                    lang
                    data {
                      title {
                        text
                      }
                      project_type
                      project_type_french
                      thumbnail {
                        alt
                        gatsbyImageData(aspectRatio: 0.71)
                      }
                      categories {
                        category {
                          document {
                            ... on PrismicActivityCategory {
                              id
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                  ... on PrismicArticle {
                    id
                    url
                    type
                    lang
                    data {
                      title {
                        text
                      }
                      thumbnail {
                        alt
                        gatsbyImageData(aspectRatio: 0.71)
                      }
                      categories {
                        category {
                          document {
                            ... on PrismicJournalCategory {
                              id
                              url
                            }
                            ... on PrismicActivityCategory {
                              id
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicGalleriesSingleDataBodyEditorialImage {
            id
            slice_type
            primary {
              image {
                alt
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
                dimensions {
                  height
                  width
                }
              }
            }
          }
          ... on PrismicGalleriesSingleDataBodyEditorialImages {
            id
            slice_type
            primary {
              editorial_image_left {
                alt
                dimensions {
                  height
                  width
                }
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
              }
              editorial_image_right {
                alt
                dimensions {
                  height
                  width
                }
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
              }
              image_left_background
              image_right_background
              invert_layout
            }
          }
          ... on PrismicGalleriesSingleDataBodyTeams {
            id
            slice_type
            primary {
              section_title {
                richText
              }
              section_text {
                richText
              }
            }
            items {
              image {
                alt
                fluid {
                  srcSet
                  src
                }
                gatsbyImageData
              }
              text {
                richText
              }
              name {
                richText
              }
            }
          }
          ... on PrismicGalleriesSingleDataBodyVideo {
            id
            slice_type
            primary {
              video {
                type
                html
                embed_url
                provider_name
                id
              }
              video_size
            }
          }
        }
      }
    }
    allPrismicEvent(
      filter: {
        lang: { eq: $lang }
        data: { categories: { elemMatch: { category: { uid: { eq: $uid } } } } }
      }
      sort: { data: { date_begins: DESC } }
    ) {
      nodes {
        _previewable
        id
        url
        type
        lang
        data {
          title {
            text
          }
          date_begins
          date_ends
          featured_img {
            alt
            gatsbyImageData(aspectRatio: 0.71)
          }
        }
      }
    }
    prismicSiteLabels(uid: { eq: "default" }, lang: { eq: $lang }) {
      data {
        labels {
          key
          value
        }
      }
    }
  }
`;

export default withPrismicPreview(GallerySingle);
